/* eslint-disable no-magic-numbers */
/* eslint-disable complexity */
import React, { useCallback, useContext, useEffect, useMemo, useReducer, useRef, useState } from 'react';
import { matchPath, useLocation, useParams } from 'react-router-dom';

import { FormattedMessage, useIntl } from '@futrli/localisation';
import { UILoader } from '@futrli/ui-components';
import { getReportingInternalReportRenderRoutePath } from '@futrli/shared-services';

import { Option, Select } from 'carbon-react/lib/components/select';

import { Filter } from '../../../../utils/table-filters/components/filter';
import { useTableFilters } from '../../../../utils/table-filters/hooks/use-table-filters/use-table-filters';
import { Data, SubmitFilterArgs, TableFiltersWithSort } from '../../../../utils/table-filters/filter-table-types';
import { CustomersSuppliersSidePanel } from './components/customers-suppliers-side-panel';
import { reducer } from './reducer';
import { ActionTypes } from './types';
import { CUSTOMER_SUPPLIER_KIND, customersAndSuppliersPeriodMap, initialFilterTableState } from './const';
import { VirtualizedList, VIRTUALIZED_LIST_TYPE } from '../../../../ui/virtualized-list/virtualized-list';
import {
  fetchCustomersAndSuppliers,
  fetchCustomersAndSuppliersDetails
} from '../../../../core/api/customers-and-suppliers';
import { PredictManagementContext } from '../../../../predict-management/context/predict-management-context';
import { trackEvent } from '../../../../core/tracker';
import { TRACKING_EVENTS } from '../../../../core/constants';
import { PERIODS } from '../components/period-dropdown';
import { getFormattedCustomersAndSuppliersData, getFormattedInitialFilters } from './utils';
import { customersSuppliersColumns } from './columns';

import * as styles from './customers-and-suppliers.scss';

const MIN_QUERY_LENGTH = 3;

const titleHeight = 48;
const headerHeight = 41.6;
const rowHeight = 32;

interface Props {
  breakWidget?: boolean;
  maxSpace?: number;
  breakSpace?: number;
  initialSpace?: number;
  zoomAdjustment?: number;
  onPeriodChanged: (p: PERIODS) => void;
  onFiltersSortChange: (data: TableFiltersWithSort) => void;
  defaultPeriod?: PERIODS;
  defaultFiltersAndSort?: TableFiltersWithSort;
}

export const CustomersAndSuppliersWidget = ({
  breakWidget,
  maxSpace = 0,
  breakSpace = 0,
  initialSpace = 0,
  zoomAdjustment = 1,
  onPeriodChanged,
  defaultPeriod,
  onFiltersSortChange,
  defaultFiltersAndSort
}: Props) => {
  const { organisationId: tempOrganisationId, workspaceId: tempWorkspaceId } = useParams();
  let organisationId = tempOrganisationId || '';
  let workspaceId = tempWorkspaceId || '';

  // Used for detecting filter changes
  const filterUpdatedCountRef = useRef(0);
  // Used for detecting sort changes
  const sortUpdatedCountRef = useRef(0);

  const location = useLocation();
  if (!organisationId || !workspaceId) {
    const path = getReportingInternalReportRenderRoutePath();
    const matchedPath = matchPath(location.pathname, path);
    if (matchedPath?.params) {
      const { organisationId: ordId, workspaceId: wsId } = matchedPath.params as Record<string, string>;
      organisationId = ordId;
      workspaceId = wsId;
    }
  }

  const { addErrorMessage } = useContext(PredictManagementContext);

  const intl = useIntl();

  const periodOptions = useMemo(
    () => [
      {
        value: PERIODS.THIS_MONTH,
        name: intl.formatMessage({ id: 'reporting.period.this_month', defaultMessage: 'This month' })
      },
      {
        value: PERIODS.LAST_MONTH,
        name: intl.formatMessage({ id: 'common.last_month', defaultMessage: 'Last month' })
      },
      {
        value: PERIODS.LAST_3_MONTHS,
        name: intl.formatMessage(
          { id: 'predict.last_n_months', defaultMessage: 'Last {n} months' },
          { n: intl.formatNumber(3) }
        )
      },
      {
        value: PERIODS.LAST_6_MONTHS,
        name: intl.formatMessage(
          { id: 'predict.last_n_months', defaultMessage: 'Last {n} months' },
          { n: intl.formatNumber(6) }
        )
      },
      {
        value: PERIODS.LAST_12_MONTHS,
        name: intl.formatMessage(
          { id: 'predict.last_n_months', defaultMessage: 'Last {n} months' },
          { n: intl.formatNumber(12) }
        )
      },
      {
        value: PERIODS.LAST_24_MONTHS,
        name: intl.formatMessage(
          { id: 'predict.last_n_months', defaultMessage: 'Last {n} months' },
          { n: intl.formatNumber(24) }
        )
      }
    ],
    [intl]
  );

  const [state, dispatch] = useReducer(reducer, {
    periodValue: defaultPeriod || PERIODS.LAST_24_MONTHS,
    isOpenSidePanel: false,
    isLoading: false,
    isLoadingDetails: false
  });

  const {
    state: filterTableState,
    setInitialData,
    setInitialFiltersAndSort,
    handleCloseFilter,
    handleOpenFilter,
    handleClearColumnFilter,
    handleSortClick,
    handleSubmit
  } = useTableFilters(initialFilterTableState);

  const {
    filterAnchorElement,
    activeColumn,
    sortByColumn,
    filters,
    sort,
    displayedData,
    filterUpdatedCount,
    sortUpdatedCount
  } = filterTableState;

  const { periodValue, activeCustomerSupplier, isOpenSidePanel, data, isLoading, isLoadingDetails } = state;

  const [paginatedDisplayedData, setPaginatedDisplayedData] = useState<Data[][]>([]);
  const [topMargins, setTopMargins] = useState<number[]>([]);
  const [topPadding, setTopPadding] = useState(0);

  const options = useMemo(
    () => [
      {
        id: CUSTOMER_SUPPLIER_KIND.CUSTOMER,
        name: intl.formatMessage({ id: 'predict.customer', defaultMessage: 'Customer' })
      },
      {
        id: CUSTOMER_SUPPLIER_KIND.SUPPLIER,
        name: intl.formatMessage({ id: 'predict.supplier', defaultMessage: 'Supplier' })
      }
    ],
    [intl]
  );

  // Formatting for pagination
  useEffect(() => {
    if (breakWidget) {
      const dd = displayedData || [];
      let addedRows = 0;
      const tMargins = [];
      const paginatedData = [];
      // check if space is enough to display table header and at least one row
      if (initialSpace < headerHeight + titleHeight + rowHeight) {
        setTopPadding(initialSpace + breakSpace);
      } else {
        setTopPadding(0);
        const n = (initialSpace - headerHeight - titleHeight) / rowHeight;
        addedRows = n;
        paginatedData.push(dd.slice(0, addedRows));
        tMargins.push((initialSpace - headerHeight - titleHeight) % rowHeight);
      }
      while (addedRows < dd.length) {
        const adjustedMaxSpace = maxSpace;
        const n = Math.floor((adjustedMaxSpace - headerHeight) / rowHeight);
        paginatedData.push(dd.slice(addedRows, addedRows + n));
        tMargins.push((adjustedMaxSpace - headerHeight) % rowHeight);
        addedRows += n;
      }
      setPaginatedDisplayedData(paginatedData);
      setTopMargins(tMargins);
    }
  }, [breakWidget, breakSpace, initialSpace, maxSpace, displayedData]);

  // Checking if there are any updates for filters and sorts. If there is any changes, callback for
  // updating is triggered
  useEffect(() => {
    // Initial filters state
    const filtersSortObject: TableFiltersWithSort = {
      filters,
      sort: {
        sort,
        sortByColumn
      }
    };

    // Flag to determine if we need to update report data
    let sendRequest = false;

    // Filters counter is different than current ref counter -> we should updated filters
    if (typeof filterUpdatedCount !== 'undefined' && filterUpdatedCount !== filterUpdatedCountRef.current) {
      filterUpdatedCountRef.current = filterUpdatedCount;
      filtersSortObject.filters = { ...filters };
      sendRequest = true;
    }

    // Sort counter is different than current ref counter -> we should updated sort
    if (typeof sortUpdatedCount !== 'undefined' && sortUpdatedCount !== sortUpdatedCountRef.current) {
      sortUpdatedCountRef.current = sortUpdatedCount;
      filtersSortObject.sort = {
        sort,
        sortByColumn
      };
      sendRequest = true;
    }

    if (sendRequest) {
      onFiltersSortChange(filtersSortObject);
    }
  }, [sort, sortByColumn, sortUpdatedCount, filterUpdatedCount, filters, onFiltersSortChange]);

  // Fetching and formatting initial data
  useEffect(() => {
    if (!periodValue) {
      return;
    }

    const getData = async () => {
      try {
        dispatch({ type: ActionTypes.SET_IS_LOADING, payload: { isLoading: true } });
        const fetchedData = await fetchCustomersAndSuppliers(workspaceId, organisationId, {
          period: customersAndSuppliersPeriodMap[periodValue as PERIODS]
        });
        dispatch({ type: ActionTypes.SET_DATA, payload: { data: getFormattedCustomersAndSuppliersData(fetchedData) } });
      } catch {
        addErrorMessage?.(new Error('Fail to fetch customers and suppliers'));
      } finally {
        dispatch({ type: ActionTypes.SET_IS_LOADING, payload: { isLoading: false } });
      }
    };

    getData();
    // addErrorMessage is not memoized and it makes constantly executing useEffect so it is removed
    // from deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, workspaceId, periodValue]);

  // Setting initial data, filters and sort
  useEffect(() => {
    if (data) {
      setInitialData((data as unknown) as Data[]);
    }

    if (defaultFiltersAndSort) {
      setInitialFiltersAndSort(
        getFormattedInitialFilters(defaultFiltersAndSort),
        defaultFiltersAndSort.sort.sort,
        defaultFiltersAndSort.sort.sortByColumn,
        false
      );
    }
  }, [data, defaultFiltersAndSort, setInitialData, setInitialFiltersAndSort]);

  const handlePeriodValueChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      dispatch({ type: ActionTypes.SET_PERIOD_VALUE, payload: { periodValue: event.target.value } });
      onPeriodChanged(event.target.value as PERIODS);
    },
    [onPeriodChanged]
  );

  const handleCustomerSupplierClick = useCallback(
    async (id: string, type?: string) => {
      try {
        if (type && id !== activeCustomerSupplier?.id) {
          dispatch({ type: ActionTypes.SET_IS_OPEN_SIDE_PANEL, payload: { isOpenSidePanel: true } });
          dispatch({ type: ActionTypes.SET_IS_LOADING_DETAILS, payload: { isLoadingDetails: true } });
          const contactDetails = await fetchCustomersAndSuppliersDetails(workspaceId, organisationId, id, {
            type
          });
          dispatch({
            type: ActionTypes.SET_ACTIVE_CUSTOMER_SUPPLIER,
            payload: { activeCustomerSupplier: contactDetails }
          });
          trackEvent(TRACKING_EVENTS.CLICKED_CUSTOMER_SUPPLIER_DETAIL);
        }
      } catch {
        addErrorMessage?.(new Error('Failed to get customers and suppliers details'));
        dispatch({ type: ActionTypes.SET_IS_OPEN_SIDE_PANEL, payload: { isOpenSidePanel: false } });
      } finally {
        dispatch({ type: ActionTypes.SET_IS_LOADING_DETAILS, payload: { isLoadingDetails: false } });
      }
    },
    // addErrorMessage is not memoized and it makes constantly executing useEffect so it is removed
    // from deps array
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [displayedData, workspaceId, organisationId, activeCustomerSupplier]
  );

  const closeSidePanel = useCallback(() => {
    dispatch({ type: ActionTypes.SET_ACTIVE_CUSTOMER_SUPPLIER, payload: { activeCustomerSupplier: undefined } });
    dispatch({ type: ActionTypes.SET_IS_OPEN_SIDE_PANEL, payload: { isOpenSidePanel: false } });
  }, []);

  const onClearColumnFilter = useCallback(() => handleClearColumnFilter(initialFilterTableState.filters), [
    handleClearColumnFilter
  ]);

  const onFilterSubmit = useCallback(
    (args: SubmitFilterArgs) => {
      handleSubmit(args);
    },
    [handleSubmit]
  );

  if (isLoading) {
    return <UILoader isFull />;
  }

  if (!displayedData) {
    return null;
  }

  return (
    <>
      <div className={styles.pageHeaderContainer} style={{ paddingTop: topPadding }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div className={styles.pageTitle}>
            <FormattedMessage id="predict.customers_and_suppliers" defaultMessage="Customers & Suppliers" />
          </div>
        </div>
        <Select
          size="medium"
          value={periodValue ?? ''}
          autoComplete="off"
          onChange={handlePeriodValueChange}
          style={{ minWidth: 174 }}
          listMaxHeight={periodOptions.length * 32}
        >
          {periodOptions.map(option => (
            <Option key={option.value} value={option.value} text={option.name} />
          ))}
        </Select>
      </div>
      {breakWidget ? (
        paginatedDisplayedData.map((d, i) => (
          <div
            key={`cAndS-chunk-${i}`}
            style={{
              paddingBottom:
                i < paginatedDisplayedData.length - 1 ? zoomAdjustment * (breakSpace / 2 + topMargins[i]) : 0,
              paddingTop: i > 0 ? zoomAdjustment * (breakSpace / 2) : 0,
              minHeight:
                i < paginatedDisplayedData.length - 1
                  ? i === 0
                    ? initialSpace * zoomAdjustment
                    : maxSpace * zoomAdjustment
                  : 'auto'
            }}
            className={styles.cAncSChunk}
          >
            <VirtualizedList
              data={d}
              handleTableFilterClick={handleOpenFilter}
              filters={filters}
              sortByColumn={sortByColumn}
              type={VIRTUALIZED_LIST_TYPE.CUSTOMERS_SUPPLIERS}
              columns={customersSuppliersColumns}
              onRowClick={handleCustomerSupplierClick}
              activeRowId={activeCustomerSupplier?.id}
              doubleHeight
            />
          </div>
        ))
      ) : (
        <VirtualizedList
          data={displayedData}
          handleTableFilterClick={handleOpenFilter}
          filters={filters}
          sortByColumn={sortByColumn}
          type={VIRTUALIZED_LIST_TYPE.CUSTOMERS_SUPPLIERS}
          columns={customersSuppliersColumns}
          onRowClick={handleCustomerSupplierClick}
          activeRowId={activeCustomerSupplier?.id}
        />
      )}

      <CustomersSuppliersSidePanel
        onClose={closeSidePanel}
        isOpen={isOpenSidePanel}
        activeCustomerSupplier={activeCustomerSupplier}
        isLoading={isLoadingDetails}
      />
      <Filter
        data={data}
        anchorElement={filterAnchorElement}
        handleSubmit={onFilterSubmit}
        handleClose={handleCloseFilter}
        handleClearColumnFilter={onClearColumnFilter}
        activeColumn={activeColumn}
        sortByColumn={sortByColumn}
        filters={filters}
        handleSortClick={handleSortClick}
        sort={sort}
        options={options}
        minQueryLength={MIN_QUERY_LENGTH}
      />

      {!displayedData ||
        (!displayedData.length && (
          <div className={styles.noUnpaidSection}>
            <h2 className="uiTypeNotificationTitleMedium">
              <FormattedMessage
                id="predict.customers_suppliers_no_unpaid"
                defaultMessage="You don’t have any unpaid customers and suppliers"
              />
            </h2>
            <p className="uiTypePopupText">
              <FormattedMessage id="predict.will_appear_when_you_do" defaultMessage="They’ll appear here when you do" />
            </p>
          </div>
        ))}
    </>
  );
};
